import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { usePricingRegion } from '../contexts/PricingRegionContext';
import { useCart } from '../contexts/CartContext';
import { ClipboardCheck, MessageCircle, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { createOrder } from '../services/api';
import { getWhatsAppLink } from '../config/contact';
import {
  buildPricingWhatsAppMessage,
  expandModuleIds,
  getModuleContent,
  getPackageContent,
  getPlanContent,
  getPricingCopy,
} from '../data/pricing';

const Cart = () => {
  const { t, isRTL, currentLang } = useLanguage();
  const {
    pricingRegion,
    formatPrice,
    localizeItemPrice,
    trackPricingEvent,
  } = usePricingRegion();
  const {
    isCartOpen,
    setIsCartOpen,
    selectedPackage,
    selectedAddons,
    selectedPlan,
    addPackage,
    removeAddon,
    addPlan,
    clearCart,
    hasItems,
  } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    phone: '',
    business: '',
  });
  const [showCustomerForm, setShowCustomerForm] = useState(false);
  const copy = getPricingCopy(currentLang);

  const getLocalizedPackageName = (pkg) =>
    getPackageContent(pkg?.canonicalName, currentLang)?.name || pkg?.name;

  const getLocalizedModuleName = (module) =>
    getModuleContent(module?.canonicalName, currentLang)?.name || module?.name;

  const getLocalizedPlanName = (plan) =>
    getPlanContent(plan?.canonicalName, currentLang)?.name || plan?.name;

  const localizeCartModule = (module) =>
    localizeItemPrice(
      {
        ...module,
        price: Number(module?.basePriceUsd ?? module?.price ?? 0),
      },
      'MODULE'
    );

  const cartAddons = selectedAddons.map(localizeCartModule);
  const cartPlan = selectedPlan
    ? localizeItemPrice(
        {
          ...selectedPlan,
          price: Number(selectedPlan.basePriceUsd ?? selectedPlan.price ?? 0),
        },
        'MONTHLY_PLAN'
      )
    : null;
  const cartPackage = selectedPackage
    ? (() => {
        if (!selectedPackage.isCustom) {
          return localizeItemPrice(
            {
              ...selectedPackage,
              price: Number(selectedPackage.basePriceUsd ?? selectedPackage.price ?? 0),
            },
            'PACKAGE'
          );
        }

        const basePackage = localizeItemPrice(
          {
            ...selectedPackage,
            price: Number(
              selectedPackage.basePriceUsd ??
              selectedPackage.basePrice ??
              selectedPackage.price ??
              0
            ),
          },
          'PACKAGE'
        );
        const modules = (selectedPackage.modules || []).map(localizeCartModule);
        const moduleTotal = modules.reduce(
          (total, module) =>
            total + Number(module.price || 0) * Number(module.quantity || 1),
          0
        );
        return {
          ...selectedPackage,
          basePrice: Number(basePackage.price || 0),
          price: Number(basePackage.price || 0) + moduleTotal,
          modules,
          pricingRegion: pricingRegion.id,
          currencyCode: pricingRegion.currencyCode,
        };
      })()
    : null;
  const cartOneTimeTotal =
    Number(cartPackage?.price || 0) +
    cartAddons.reduce(
      (total, addon) =>
        total + Number(addon.price || 0) * Number(addon.quantity || 1),
      0
    );
  const cartMonthlyTotal = Number(cartPlan?.price || 0);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCustomerInfo((current) => ({ ...current, [name]: value }));
  };

  const handleCheckout = async () => {
    if (!selectedPackage) {
      toast.error(copy.selectPackageError);
      return;
    }
    if (!selectedPlan) {
      toast.error(copy.selectPlanError);
      return;
    }
    if (!customerInfo.name || !customerInfo.email || !customerInfo.business) {
      setShowCustomerForm(true);
      return;
    }

    setIsProcessing(true);

    const isCustom = Boolean(selectedPackage.isCustom);
    const selectedModuleIds = isCustom
      ? selectedPackage.selectedModuleIds || []
      : expandModuleIds(selectedAddons);

    const orderData = {
      package_id: selectedPackage.id || null,
      is_custom_package: isCustom,
      selected_module_ids: selectedModuleIds.filter(Boolean),
      plan_id: selectedPlan.id || null,
      customer_name: customerInfo.name,
      customer_email: customerInfo.email,
      customer_phone: customerInfo.phone || null,
      customer_business: customerInfo.business,
      language: currentLang,
      pricing_region: pricingRegion.id,
      currency_code: pricingRegion.currencyCode,
      displayed_one_time_total: cartOneTimeTotal,
      displayed_monthly_total: cartMonthlyTotal,
    };

    const localizedPackage = {
      ...cartPackage,
      name: getLocalizedPackageName(cartPackage),
      modules: (cartPackage?.modules || []).map((module) => ({
        ...module,
        name: getLocalizedModuleName(module),
      })),
    };
    const localizedAddons = cartAddons.map((addon) => ({
      ...addon,
      name: getLocalizedModuleName(addon),
    }));
    const localizedPlan = {
      ...cartPlan,
      name: getLocalizedPlanName(cartPlan),
    };

    const message = buildPricingWhatsAppMessage({
      language: currentLang,
      selectedPackage: localizedPackage,
      selectedAddons: localizedAddons,
      selectedPlan: localizedPlan,
      customerInfo,
      oneTimeTotal: cartOneTimeTotal,
      monthlyTotal: cartMonthlyTotal,
      pricingRegion: pricingRegion.id,
      currencyCode: pricingRegion.currencyCode,
      displayedOneTimeTotal: cartOneTimeTotal,
      displayedMonthlyTotal: cartMonthlyTotal,
      formatPrice,
    });

    trackPricingEvent('pricing_request_started', {
      oneTimeTotal: cartOneTimeTotal,
      monthlyTotal: cartMonthlyTotal,
    });

    window.open(
      getWhatsAppLink(message),
      '_blank',
      'noopener,noreferrer'
    );

    try {
      await createOrder(orderData);
    } catch (error) {
      console.error('Request could not be saved:', error);
      toast.warning(copy.saveWarning);
    } finally {
      setIsCartOpen(false);
      clearCart();
      setIsProcessing(false);
      setShowCustomerForm(false);
      setCustomerInfo({ name: '', email: '', phone: '', business: '' });
      toast.success(copy.requestReady);
    }
  };

  if (!isCartOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50"
        onClick={() => setIsCartOpen(false)}
      />

      <div
        className={`fixed top-0 bottom-0 w-full max-w-md z-50 overflow-y-auto ${
          isRTL ? 'left-0 border-r' : 'right-0 border-l'
        }`}
        style={{
          direction: isRTL ? 'rtl' : 'ltr',
          background: 'rgba(18, 18, 18, 0.95)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderColor: 'rgba(255, 255, 255, 0.1)',
        }}
      >
        <div className="sticky top-0 bg-[#121212] border-b border-white/10 p-6 flex items-center justify-between z-10">
          <h2 className="text-2xl font-semibold text-white">
            {t('cart.title')}
          </h2>
          <button
            onClick={() => setIsCartOpen(false)}
            className="p-2 text-white/60 hover:text-white transition-colors"
            aria-label={copy.remove}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {!hasItems() ? (
            <p className="text-white/60 text-center py-12">{t('cart.empty')}</p>
          ) : (
            <>
              {cartPackage && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white/60 text-sm uppercase tracking-wide">
                      {cartPackage.isCustom
                        ? copy.cartCustomPackage
                        : t('cart.package')}
                    </h3>
                    <button
                      onClick={() => addPackage(null)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      data-testid="remove-package-btn"
                      aria-label={copy.remove}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div
                    className="glass-card p-4"
                    style={{ borderRadius: '12px' }}
                  >
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <div>
                        <span className="text-white font-medium block">
                          {getLocalizedPackageName(cartPackage)}
                        </span>
                        <span className="text-white/40 text-xs">
                          {copy.oneTime}
                        </span>
                      </div>
                      <span className="price-value text-[#00FFD1] font-bold whitespace-nowrap">
                        {formatPrice(cartPackage.price)}
                      </span>
                    </div>

                    {cartPackage.isCustom &&
                      cartPackage.modules?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-white/10">
                          <div className="flex items-center justify-between text-xs mb-3">
                            <span className="text-white/50">
                              {copy.basePrice}
                            </span>
                            <span className="price-value text-white/60">
                              {formatPrice(cartPackage.basePrice)}
                            </span>
                          </div>
                          <p className="text-white/50 text-xs mb-2">
                            {copy.selectedModules}:
                          </p>
                          <div className="space-y-2">
                            {cartPackage.modules.map((module) => {
                              const quantity = module.quantity || 1;
                              return (
                                <div
                                  key={module.id}
                                  className="flex items-start justify-between gap-3 text-sm"
                                >
                                  <span className="text-white/70 min-w-0">
                                    {getLocalizedModuleName(module)}
                                    {quantity > 1 ? ` × ${quantity}` : ''}
                                  </span>
                                  <span className="price-value text-white/50 whitespace-nowrap">
                                    {formatPrice(Number(module.price || 0) * quantity)}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                  </div>
                </div>
              )}

              {cartAddons.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-white/60 text-sm uppercase tracking-wide">
                    {t('cart.addons')}
                  </h3>
                  <div className="space-y-2">
                    {cartAddons.map((addon) => {
                      const quantity = addon.quantity || 1;
                      return (
                        <div
                          key={addon.id}
                          className="glass-card p-4 flex items-start justify-between gap-3"
                          style={{ borderRadius: '12px' }}
                        >
                          <div className="min-w-0">
                            <span className="text-white block">
                              {getLocalizedModuleName(addon)}
                            </span>
                            {quantity > 1 && (
                              <span className="text-white/45 text-xs">
                                {copy.quantity}: {quantity}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="price-value text-[#00FFD1] font-bold whitespace-nowrap">
                              {formatPrice(Number(addon.price || 0) * quantity)}
                            </span>
                            <button
                              onClick={() => removeAddon(addon.id)}
                              className="text-red-400 hover:text-red-300 transition-colors"
                              aria-label={copy.remove}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {cartPlan && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-white/60 text-sm uppercase tracking-wide">
                      {t('cart.plan')}
                    </h3>
                    <button
                      onClick={() => addPlan(null)}
                      className="text-red-400 hover:text-red-300 transition-colors"
                      aria-label={copy.remove}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                  <div
                    className="glass-card p-4 flex items-center justify-between gap-3"
                    style={{ borderRadius: '12px' }}
                  >
                    <span className="text-white font-medium">
                      {getLocalizedPlanName(cartPlan)}
                    </span>
                    <span className="price-value text-[#00FFD1] font-bold whitespace-nowrap">
                      {formatPrice(cartPlan.price)}
                      {copy.perMonth}
                    </span>
                  </div>
                </div>
              )}

              <div className="border-t border-white/10 pt-6 space-y-4">
                <div className="flex items-center justify-between gap-4">
                  <span className="text-white/60">{t('cart.oneTime')}</span>
                  <span className="price-value text-white text-xl font-bold whitespace-nowrap">
                    {formatPrice(cartOneTimeTotal)}
                  </span>
                </div>
                {cartMonthlyTotal > 0 && (
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-white/60">{t('cart.monthly')}</span>
                    <span className="price-value text-[#00FFD1] text-xl font-bold whitespace-nowrap">
                      {formatPrice(cartMonthlyTotal)}
                      {copy.perMonth}
                    </span>
                  </div>
                )}
              </div>

              {showCustomerForm && (
                <div className="space-y-4 border-t border-white/10 pt-6">
                  <h4 className="text-white font-medium">
                    {copy.yourInformation}
                  </h4>
                  <input
                    type="text"
                    name="name"
                    value={customerInfo.name}
                    onChange={handleInputChange}
                    placeholder={t('form.name')}
                    required
                    className="glass-input"
                  />
                  <input
                    type="email"
                    name="email"
                    value={customerInfo.email}
                    onChange={handleInputChange}
                    placeholder={t('form.email')}
                    required
                    className="glass-input"
                  />
                  <input
                    type="tel"
                    name="phone"
                    value={customerInfo.phone}
                    onChange={handleInputChange}
                    placeholder={t('form.phone')}
                    className="glass-input"
                  />
                  <input
                    type="text"
                    name="business"
                    value={customerInfo.business}
                    onChange={handleInputChange}
                    placeholder={t('form.business')}
                    required
                    className="glass-input"
                  />
                </div>
              )}

              <button
                onClick={handleCheckout}
                disabled={isProcessing}
                className="w-full btn-glass-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isProcessing ? (
                  <div className="w-6 h-6 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    <MessageCircle size={20} />
                    {t('cart.checkout')}
                  </>
                )}
              </button>

              <div
                className="glass-card p-3 flex items-center justify-center gap-2 text-white/50 text-sm"
                style={{ borderRadius: '10px' }}
              >
                <ClipboardCheck size={16} className="text-[#00FFD1]" />
                <span>{t('footer.secure')}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default Cart;
